import _objectWithoutProperties from "/runner/_work/miro-site/miro-site/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/objectWithoutProperties.js";
import _defineProperty from "/runner/_work/miro-site/miro-site/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";
var _excluded = ["className", "spacingGroup", "color", "style", "media", "children", "floatingOrnaments", "animationType", "mediaAspectRatio"],
    _excluded2 = ["className", "spacingGroup", "color", "style", "media"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import classNames from 'classnames';
import { motion } from 'framer-motion';
import React, { forwardRef, lazy, Suspense } from 'react';
import Default from 'react-bootstrap/Container';
import { Media } from '../../media/media';
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var FloatingOrnaments = /*#__PURE__*/lazy(function () {
  return import('../floating-ornaments/floating-ornaments');
});
export var AnimationPresets = {
  fadeInScaleDown: 'fadeInScaleDown'
};

/**
 * This function will return a CSSStyleDeclaration object that can be used
 * to set the background color of the container and allow children components
 * to access the background color of the container via the
 * `--wcds-container-background-color` CSS variable.
 */
var getContainerCSSDeclaration = function getContainerCSSDeclaration() {
  var style = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

  if (!style) {
    return {};
  }

  return {
    /**
     * Import SASS Variable
     */
    '--wcds-container-background-color': (style === null || style === void 0 ? void 0 : style.backgroundColor) && "var(".concat(style.backgroundColor, ")"),
    '--wcds-container-color': (style === null || style === void 0 ? void 0 : style.color) && "var(".concat(style === null || style === void 0 ? void 0 : style.color, ")"),
    backgroundColor: (style === null || style === void 0 ? void 0 : style.backgroundColor) && "var(--wcds-container-background-color)",
    color: (style === null || style === void 0 ? void 0 : style.color) && "var(--wcds-container-color)",
    cursor: style === null || style === void 0 ? void 0 : style.cursor
  };
};

var BackgroundImage = function BackgroundImage(_ref) {
  var media = _ref.media;

  if (!media) {
    return /*#__PURE__*/_jsx(_Fragment, {});
  }

  return /*#__PURE__*/_jsx("div", {
    className: "position-absolute w-100 h-100 bg-black",
    children: /*#__PURE__*/_jsx(Media, {
      hideBorder: true,
      media: media,
      className: "w-100 h-100 opacity-40",
      objectFit: "cover"
    })
  });
};
/**
 * Gradually fades in the element and reduces its scale from 1.05 to 1 when the
 * component is scrolled into view.
 */


var fadeInScaleDownAnimation = {
  initial: {
    opacity: 0,
    y: 48,
    scale: 1.05
  },
  whileInView: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      ease: [0.16, 1, 0.3, 1],
      duration: 2
    }
  },
  viewport: {
    once: true
  }
};
var animations = {
  fadeInScaleDown: fadeInScaleDownAnimation
};

var AnimationContainer = function AnimationContainer(_ref2) {
  var animationType = _ref2.animationType,
      children = _ref2.children;
  var animation;

  if (animationType && animations[animationType]) {
    animation = animations[animationType];
  }

  if (!animationType || !animation) {
    return /*#__PURE__*/_jsx(_Fragment, {
      children: children
    });
  }

  return /*#__PURE__*/_jsx(motion.div, _objectSpread(_objectSpread({}, animation), {}, {
    children: children
  }));
};
/**
 * Prevent scroll issues from off-screen floating ornaments
 */


var FloatingOrnamentsContainer = function FloatingOrnamentsContainer(_ref3) {
  var children = _ref3.children;
  return /*#__PURE__*/_jsx("div", {
    className: "overflow-hidden",
    children: children
  });
};
/**
 * This Container is our enhanced version of the Bootstrap Container component
 * that will provide us with consistent layout options across the application.
 *
 * The main enhancements for this component are the ability to set a background
 * color for fluid containers whilst maintaining the max width of the breakpoint.
 */


export var _Container = /*#__PURE__*/forwardRef(function (_ref4, ref) {
  var className = _ref4.className,
      spacingGroup = _ref4.spacingGroup,
      color = _ref4.color,
      style = _ref4.style,
      media = _ref4.media,
      children = _ref4.children,
      floatingOrnaments = _ref4.floatingOrnaments,
      animationType = _ref4.animationType,
      mediaAspectRatio = _ref4.mediaAspectRatio,
      rest = _objectWithoutProperties(_ref4, _excluded);

  var hasFloatingOrnaments = !!floatingOrnaments && floatingOrnaments.length > 0;
  var Wrapper = hasFloatingOrnaments ? FloatingOrnamentsContainer : React.Fragment;
  return /*#__PURE__*/_jsx(Wrapper, {
    children: /*#__PURE__*/_jsx(Default, _objectSpread(_objectSpread({
      className: classNames('position-relative', className, spacingGroup),
      ref: ref,
      style: getContainerCSSDeclaration(style)
    }, rest), {}, {
      children: /*#__PURE__*/_jsxs(AnimationContainer, {
        animationType: animationType,
        children: [/*#__PURE__*/_jsx(BackgroundImage, {
          media: media
        }), hasFloatingOrnaments ? /*#__PURE__*/_jsx(Suspense, {
          fallback: /*#__PURE__*/_jsx(_Fragment, {}),
          children: /*#__PURE__*/_jsx(FloatingOrnaments, {
            floatingOrnaments: floatingOrnaments
          })
        }) : undefined, children]
      })
    }))
  });
});
var FluidWithBackground = /*#__PURE__*/forwardRef(function (_ref5, ref) {
  var className = _ref5.className,
      spacingGroup = _ref5.spacingGroup,
      color = _ref5.color,
      style = _ref5.style,
      media = _ref5.media,
      rest = _objectWithoutProperties(_ref5, _excluded2);

  return /*#__PURE__*/_jsxs(Default, {
    fluid: true,
    ref: ref,
    className: "position-relative p-0",
    style: getContainerCSSDeclaration(style),
    children: [/*#__PURE__*/_jsx(BackgroundImage, {
      media: media
    }), /*#__PURE__*/_jsx(Container, _objectSpread({
      className: classNames(className, spacingGroup)
    }, rest))]
  });
});
var customComponents = {
  /**
   * Custom components
   */
  FluidWithBackground: FluidWithBackground
};
export var Container = Object.assign(_Container, customComponents);